import { fetchUserAttributes } from "@aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import { useEffect, useMemo, useState } from "react";
import {
  listGeneralInformations,
  listNotifications,
  listUserLinks,
} from "../graphql/queries.js";
import { assessmentQuestionLabelByKey } from "./Assessment.js";

export default function ProfessionalClientsRecommendations() {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [filteredRecommendations, setFilteredRecommendations] = useState([]);
  const [selectedClientEmail, setSelectedClientEmail] = useState("");
  const [, setProfessionalEmail] = useState("");
  const [clientNameByEmail, setClientNameByEmail] = useState({});

  const sortRecommendationsByDateDesc = (items = []) => {
    return [...items].sort((a, b) => {
      const aDate = new Date(
        a?.RecommendationDate || a?.createdAt || "1970-01-01",
      ).getTime();
      const bDate = new Date(
        b?.RecommendationDate || b?.createdAt || "1970-01-01",
      ).getTime();
      return bDate - aDate;
    });
  };

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // Get current authenticated user using your helper
        const attr = await fetchUserAttributes();
        const email = String(attr.email || "")
          .trim()
          .toLowerCase();
        setProfessionalEmail(email);

        // Create GraphQL client
        const graphqlClient = generateClient();

        // Query linked clients
        const userLinksRes = await graphqlClient.graphql({
          query: listUserLinks,
          variables: { filter: { professionalEmail: { eq: email } } },
        });
        const linkedClientsRaw = userLinksRes.data.listUserLinks.items;
        const linkedClients = (linkedClientsRaw || []).filter(
          (l) => !l.unlinkedAt,
        );
        setClients(linkedClients);

        // Build a name lookup from GeneralInformation using stable userId (clientId).
        // This allows the UI to show correct names even when client email changes.
        const uniqueClientIds = Array.from(
          new Set(
            (linkedClients || [])
              .map((l) => String(l?.clientId || "").trim())
              .filter(Boolean),
          ),
        );

        const nameMap = {};

        const chunkSize = 20;
        for (let i = 0; i < uniqueClientIds.length; i += chunkSize) {
          const ids = uniqueClientIds.slice(i, i + chunkSize);
          const res = await graphqlClient.graphql({
            query: listGeneralInformations,
            variables: {
              filter: {
                or: ids.map((id) => ({ userId: { eq: id } })),
              },
              limit: 200,
            },
          });

          const items = res?.data?.listGeneralInformations?.items || [];
          for (const gi of items) {
            const giEmail = String(gi?.Email || "")
              .trim()
              .toLowerCase();
            if (!giEmail) continue;
            const fullName =
              `${gi?.FirstName || ""} ${gi?.LastName || ""}`.trim();
            if (fullName) nameMap[giEmail] = fullName;
          }
        }

        setClientNameByEmail(nameMap);

        // Query recommendations
        const recsRes = await graphqlClient.graphql({
          query: listNotifications,
          variables: {
            filter: {
              NotificationType: { eq: "Recommendation" },
              SenderEmail: { eq: email },
            },
            limit: 200,
          },
        });
        const recs = recsRes?.data?.listNotifications?.items || [];
        const sortedRecs = sortRecommendationsByDateDesc(recs);
        setRecommendations(sortedRecs);
        setFilteredRecommendations(sortedRecs);
      } catch (error) {
        console.error("Error loading data:", error);
        // Optional: you might want to sign out user on auth failure
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  function formatStatus(status) {
    const s = String(status || "").trim();
    if (!s) return "N/A";
    const lower = s.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  function formatRecommendationResult(result) {
    const key = String(result || "").trim();
    if (!key) return "N/A";
    return assessmentQuestionLabelByKey?.[key] || key;
  }

  function getClientNameByEmail(email) {
    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();

    const fromGeneralInfo = clientNameByEmail?.[normalizedEmail];
    if (fromGeneralInfo) return fromGeneralInfo;

    const client = clients.find(
      (c) => c.clientEmail.toLowerCase() === email.toLowerCase(),
    );
    return client
      ? `${client.clientFirstName} ${client.clientLastName}`
      : "(Unknown Client)";
  }

  const clientEmailOptions = useMemo(() => {
    const emails = new Set();

    for (const rec of recommendations || []) {
      const e = String(rec?.TargetEmail || "")
        .trim()
        .toLowerCase();
      if (e) emails.add(e);
    }

    for (const c of clients || []) {
      const e = String(c?.clientEmail || "")
        .trim()
        .toLowerCase();
      if (e) emails.add(e);
    }

    return Array.from(emails);
  }, [clients, recommendations]);

  // Filter recommendations by client email
  useEffect(() => {
    if (!selectedClientEmail) {
      setFilteredRecommendations(
        sortRecommendationsByDateDesc(recommendations),
      );
    } else {
      setFilteredRecommendations(
        sortRecommendationsByDateDesc(
          recommendations.filter(
            (rec) =>
              rec.TargetEmail.toLowerCase() ===
              selectedClientEmail.toLowerCase(),
          ),
        ),
      );
    }
  }, [selectedClientEmail, recommendations]);

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div
        style={{
          width: 350,
          minWidth: 300,
          margin: "0 auto",
          alignItems: "center",
          backgroundColor: "white",
          padding: 24,
          borderRadius: 12,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          color: "#333",
        }}
      >
        <img
          src={(process.env.PUBLIC_URL || "") + "/Icon-CPR-Blue.png"}
          alt="Logo"
          style={{
            width: "100%",
            maxWidth: 200,
            height: "auto",
            display: "block",
            margin: "5px auto 20px auto",
          }}
        />
        <h2>Recommendations Sent to Clients</h2>

        <label>
          Filter by client:
          <select
            value={selectedClientEmail}
            onChange={(e) => setSelectedClientEmail(e.target.value)}
            style={{
              marginLeft: 10,
              marginRight: 10,
              maxWidth: "calc(100% - 20px)",
              overflow: "hidden",
            }}
          >
            <option value="">-- All Clients --</option>
            {clientEmailOptions.map((email) => (
              <option key={email} value={email}>
                {getClientNameByEmail(email)} ({email})
              </option>
            ))}
          </select>
        </label>

        <h3 style={{ marginTop: 20 }}>
          Showing {filteredRecommendations.length} Recommendation
          {filteredRecommendations.length !== 1 ? "s" : ""}
        </h3>

        {filteredRecommendations.length === 0 && (
          <p>No recommendations found.</p>
        )}

        <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
          {filteredRecommendations.map((rec) => (
            <li
              key={rec.id}
              style={{
                marginBottom: 15,
                borderBottom: "1px solid #ccc",
                paddingBottom: 10,
                textAlign: "left",
              }}
            >
              <strong>To:</strong> {getClientNameByEmail(rec.TargetEmail)} (
              {rec.TargetEmail}) <br />
              <strong>Date:</strong> {rec.RecommendationDate || "N/A"} <br />
              <strong>Type:</strong> {rec.RecommendationType || "N/A"} <br />
              <strong>Action:</strong> {rec.RecommendationAction || "N/A"}{" "}
              <br />
              <strong>Frequency:</strong>{" "}
              {rec.RecommendationActionFrequency || "N/A"} <br />
              <strong>Assessment Question:</strong>{" "}
              {formatRecommendationResult(rec.RecommendationResult)} <br />
              <strong>Note:</strong> {rec.RecommendationNote || "N/A"} <br />
              <strong>Status:</strong> {formatStatus(rec.Status)} <br />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
